import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, ListGroup, Badge } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaShoppingBag, FaHome, FaPrint, FaDownload, FaEnvelope } from 'react-icons/fa';
import Layout from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import OrderProgress from '../components/OrderProgress';
import api from '../services/api';

// Mock order data
const MOCK_ORDERS = [
  {
    id: '1',
    items: [
      {
        id: '1',
        name: 'Textbook - Introduction to Computer Science',
        price: 50.00,
        quantity: 1,
        image: 'https://via.placeholder.com/100x70?text=Textbook'
      },
      {
        id: '2',
        name: 'Scientific Calculator',
        price: 25.00,
        quantity: 1,
        image: 'https://via.placeholder.com/100x70?text=Calculator'
      }
    ],
    total: 75.00,
    status: 'confirmed',
    paymentMethod: 'mobile-money',
    paymentStatus: 'paid',
    date: '2023-06-30T10:30:00Z',
    deliveryInfo: {
      name: 'John Doe',
      phoneNumber: '+233 XX XXX XXXX',
      email: 'john.doe@example.com',
      location: 'Block A Hostel'
    }
  }
];

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentVerified, setPaymentVerified] = useState(false);

  // On mount: check for Paystack reference and verify payment
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get('reference');
    async function verifyAndFetch() {
      setLoading(true);
      setError('');
      try {
        if (reference) {
          await api.post('/orders/verify-payment/', { reference });
          setPaymentVerified(true);
        }
        // Fetch real order data from backend
        const res = await api.get(`/orders/${orderId}/`);
        setOrder(res.data);
      } catch (err) {
        setError('Failed to verify payment or fetch order.');
      } finally {
        setLoading(false);
      }
    }
    verifyAndFetch();
  }, [orderId]);
  
  const handlePrint = () => {
    window.print();
  };
  
  const handleDownloadInvoice = () => {
    // In a real app, this would generate and download a PDF invoice
    alert('Invoice download functionality would be implemented here');
  };
  
  if (loading) {
    return (
      <Layout>
        <Container className="py-5 text-center">
          <h3>Loading order details...</h3>
        </Container>
      </Layout>
    );
  }
  
  if (error || !order) {
    return (
      <Layout>
        <Container className="py-5">
          <Alert variant="danger">
            {error || 'Order not found'}
          </Alert>
          <div className="text-center mt-4">
            <Button as={Link} to="/products" variant="primary">
              Continue Shopping
            </Button>
          </div>
        </Container>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <Container className="py-4">
        <Card className="shadow-sm mb-4 order-confirmation-card">
          <Card.Body className="text-center p-5">
            <FaCheckCircle className="text-success mb-3" size={60} />
            <h2 className="mb-3">Order Confirmed!</h2>
            <p className="lead mb-2">Thank you for your order.</p>
            <p className="mb-4">Your order has been received and is now being processed.</p>
            <div className="order-id-box mx-auto mb-4">
              <h5 className="mb-1">Order ID</h5>
              <h3 className="mb-0">#{order.id}</h3>
            </div>
            <p className="text-muted">
              A confirmation has been sent to your email: {currentUser?.email || order.deliveryInfo.email}
            </p>
          </Card.Body>
        </Card>
        {/* Order Progress Stepper */}
        <div className="order-progress-container mb-5">
          <OrderProgress status={order.status} />
        </div>
        <Row>
          {/* Order Details */}
          <Col lg={8}>
            <Card className="shadow-sm mb-4">
              <Card.Header className="bg-white">
                <h4 className="mb-0">
                  <FaShoppingBag className="me-2" />
                  Order Details
                </h4>
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between mb-3">
                  <div>
                    <strong>Order Date:</strong> {new Date(order.created_at).toLocaleString()}
                  </div>
                  <div>
                    <Badge 
                      bg={
                        order.status === 'confirmed' ? 'success' : 
                        order.status === 'processing' ? 'primary' : 
                        order.status === 'delivered' ? 'info' : 'secondary'
                      }
                      className="p-2"
                    >
                      {order.status.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                
                <ListGroup variant="flush" className="mb-4">
                  {order.items.map(item => (
                    <ListGroup.Item key={item.id} className="py-3">
                      <Row className="align-items-center">
                        <Col xs={3} md={2}>
                          <img 
                            src={item.image} 
                            alt={item.name} 
                            className="img-fluid rounded"
                          />
                        </Col>
                        <Col xs={9} md={6}>
                          <h5 className="mb-1">{item.name}</h5>
                          <div className="text-muted small">
                            Unit Price: GH₵ {Number(item.price || 0).toFixed(2)}
                          </div>
                        </Col>
                        <Col xs={6} md={2} className="text-center mt-3 mt-md-0">
                          <div className="text-muted">
                            Qty: {item.quantity}
                          </div>
                        </Col>
                        <Col xs={6} md={2} className="text-end mt-3 mt-md-0">
                          <div className="fw-bold">
                            GH₵ {Number(item.price || 0) * Number(item.quantity || 1) ? (Number(item.price || 0) * Number(item.quantity || 1)).toFixed(2) : '0.00'}
                          </div>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
                
                <Row className="mb-3">
                  <Col md={6}>
                    <h5 className="mb-3">Payment Information</h5>
                    <div className="mb-2">
                      <strong>Payment Method:</strong> {order.payment_method === 'mobile-money' ? 'Mobile Money' :
  order.payment_method === 'cash' ? 'Cash on Delivery' :
  order.payment_method === 'paystack' ? 'Paystack' :
  order.payment_method === 'bank-transfer' ? 'Bank Transfer' : (order.payment_method || 'Unknown')}
                    </div>
                    <div className="mb-2">
                      <strong>Payment Status:</strong> {' '}
                      <Badge 
                        bg={order.payment_status === 'paid' ? 'success' : order.payment_status === 'failed' ? 'danger' : order.payment_status === 'refunded' ? 'secondary' : 'warning'}
                      >
                        {(order.payment_status || '').toString().toUpperCase() || 'UNKNOWN'}
                      </Badge>
                    </div>
                  </Col>
                  <Col md={6}>
                    <h5 className="mb-3">Delivery Information</h5>
                    <div className="mb-2">
                      <strong>Name:</strong> {currentUser?.name || (order.deliveryInfo ? order.deliveryInfo.name : '')}
                    </div>
                    <div className="mb-2">
                      <strong>Phone:</strong> {currentUser?.phoneNumber || (order.deliveryInfo ? order.deliveryInfo.phoneNumber : '')}
                    </div>
                    <div className="mb-2">
                      <strong>Location:</strong> {currentUser?.location || (order.deliveryInfo ? order.deliveryInfo.location : '')}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
              <Card.Footer className="bg-white">
                <div className="d-flex flex-wrap justify-content-between">
                  <Button 
                    variant="outline-secondary" 
                    className="mb-2 mb-md-0"
                    onClick={handlePrint}
                  >
                    <FaPrint className="me-2" />
                    Print
                  </Button>
                  
                </div>
              </Card.Footer>
            </Card>
          </Col>
          
          {/* Order Summary */}
          <Col lg={4}>
            <Card className="shadow-sm mb-4">
              <Card.Header className="bg-white">
                <h4 className="mb-0">Order Summary</h4>
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between mb-2">
                  <span>Subtotal</span>
                  <span>GH₵ {Number(order.total || 0).toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Delivery Fee</span>
                  <span>GH₵ 0.00</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between fw-bold mb-0">
                  <span>Total</span>
                  <span>GH₵ {Number(order.total || 0).toFixed(2)}</span>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white">
                <div className="d-grid gap-2">
                  <Button as={Link} to="/products" variant="primary">
                    <FaHome className="me-2" />
                    Continue Shopping
                  </Button>
                  <Button as={Link} to={`/order/${order.id}`} variant="outline-primary">
                    <FaShoppingBag className="me-2" />
                    View Order Details
                  </Button>
                </div>
              </Card.Footer>
            </Card>
            
            <Card className="shadow-sm">
              <Card.Body>
                <h5 className="mb-3">What Happens Next?</h5>
                <ol className="ps-3">
                  <li className="mb-2">The seller will be notified of your order.</li>
                  <li className="mb-2">You will receive the seller's contact details via email.</li>
                  <li className="mb-2">Arrange a meeting time and place with the seller on campus.</li>
                  <li className="mb-2">Meet the seller to collect your items.</li>
                  <li>After receiving your items, please mark the order as complete.</li>
                </ol>
                <Alert variant="info" className="mb-0 mt-3">
                  <p className="mb-0">
                    <strong>Need help?</strong> If you have any questions about your order, please contact our support team at <a href="mailto:support@unitrade.com">support@unitrade.com</a>.
                  </p>
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default OrderConfirmation;
